import React, { useState } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Link } from "react-router-dom";
import { useApp } from "@/store/AppContext";
import PortalLoginDropdown from "@/components/PortalLoginDropdown";

export default function Header() {
  const [open, setOpen] = useState(false);
  const { theme, toggleTheme, openContact } = useApp();

  const scrollTo = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-app/85 border-b border-[var(--card-border)]"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-12 h-24">
        <a href="/" data-testid="nav-brand-logo" className="flex items-center gap-3 group">
          <img
            src="/logo.png"
            alt="NS-I"
            className="h-14 sm:h-16 w-auto logo-blend"
          />
          <span className="font-serif text-2xl tracking-tight text-app font-bold">NS-I</span>
        </a>

        <nav className="hidden md:flex items-center gap-7">
          {[
            ["Services", "services"],
            ["Destinations", "destinations"],
            ["Testimonials", "testimonials"],
          ].map(([label, id]) => (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              data-testid={`nav-link-${id}`}
              className="text-sm text-app-muted hover:text-app transition-colors"
            >
              {label}
            </button>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="text-sm text-app-muted hover:text-app transition-colors"
          >
            Journal
          </Link>
          <button
            onClick={() => openContact()}
            data-testid="nav-contact-btn"
            className="text-sm text-app-muted hover:text-app transition-colors"
          >
            Contact Us
          </button>

          <button
            onClick={toggleTheme}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            title={theme === "light" ? "Switch to dark" : "Switch to light"}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full border-2 border-[#C9A961]/60 bg-[#0B1E3F]/10 text-[#0B1E3F] hover:bg-[#C9A961]/20 hover:border-[#C9A961] transition shadow-sm"
          >
            {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4 text-[#F8E6B0]" />}
          </button>

          <PortalLoginDropdown />
          <button
            onClick={() => openContact()}
            data-testid="nav-start-app-btn"
            className="rounded-full px-5 py-2 text-sm font-semibold bg-[#0B1E3F] text-white hover:bg-[#162A4D] transition"
          >
            Start Application
          </button>
        </nav>

        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-mobile"
            className="w-10 h-10 rounded-full border border-[var(--card-border)] flex items-center justify-center"
          >
            {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="w-10 h-10 rounded-full border border-[var(--card-border)] flex items-center justify-center"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-app border-t border-[var(--card-border)] px-4 py-6 space-y-4"
        >
          {[
            ["Services", "services"],
            ["Destinations", "destinations"],
            ["Testimonials", "testimonials"],
          ].map(([label, id]) => (
            <button key={id} onClick={() => scrollTo(id)} className="block w-full text-left text-app">
              {label}
            </button>
          ))}
          <Link to="/blog" onClick={() => setOpen(false)} className="block text-app">
            Journal
          </Link>
          <button onClick={() => { setOpen(false); openContact(); }} className="block w-full text-left text-app">
            Contact Us
          </button>
          <div className="pt-2 border-t border-[var(--card-border)]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-app-muted mb-2">Sign in</div>
            <Link to="/applicant/login" className="block py-1.5 text-sm text-app">Applicant portal</Link>
            <Link to="/student/login" className="block py-1.5 text-sm text-app">Student portal</Link>
            <Link to="/crm/login" className="block py-1.5 text-sm text-app">Staff dashboard</Link>
          </div>
          <button
            onClick={() => { setOpen(false); openContact(); }}
            className="w-full rounded-full py-3 font-semibold bg-[#0B1E3F] text-white"
          >
            Start Application
          </button>
        </div>
      )}

      <style>{`
        .logo-blend { mix-blend-mode: multiply; }
        html[data-theme="dark"] .logo-blend {
          mix-blend-mode: screen;
          filter: invert(0.95) hue-rotate(180deg) brightness(1.1);
        }
      `}</style>
    </header>
  );
}
